import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { resolveTabs } from '../../i18n/resolveTabs';
import HeadcountSummaryPage from './HeadcountSummaryPage';
import HrHeadcountPage from './HrHeadcountPage';
import HrPlanningPage from './HrPlanningPage';

/* Effectif — l'effectif CONSTATÉ et l'effectif PLANIFIÉ, côte à côte
   (2026-08-27). Ils vivaient chacun dans une autre section : le réel dans
   « Chiffres clés », le plan dans « Planification ». On compare un effectif à
   un effectif, pas à une masse salariale — la question « combien sommes-nous,
   combien serons-nous » se lit d'un écran.

   Gabarit : <PageShell framed> + <Tabs> + sous-pages en embedded. */
const TAB_DEFS = [
  /* La SYNTHÈSE ouvre la section (2026-08-29) : où j'en suis, où je vais, ce
     qui bouge. Les onglets suivants montrent le détail qu'elle résume. */
  { id: 'summary', labelKey: 'people:headcount.tabs.summary' },
  { id: 'actual',  labelKey: 'people:headcount.tabs.actual' },
  /* Les MOUVEMENTS ont leur onglet (2026-08-27) : ce qu'on simule n'est pas
     ce qu'on planifie. Il porte la variation des salaires bruts, qui montre
     l'effet de ces mouvements mois par mois. */
  { id: 'moves',   labelKey: 'people:headcount.tabs.moves' },
  { id: 'planned', labelKey: 'people:headcount.tabs.planned' },
];

export default function HeadcountHub({ activeTenant, setPage, addToast, year, onYearChange, onGoPeople,
                                       intent = null, onIntentConsumed }) {
  const { t } = useTranslation();
  const p = useP();
  const [tab, setTab] = useState('summary');
  /* Intention passée à l'onglet Mouvements : bascule + modale ouverte. */
  const [moveIntent, setMoveIntent] = useState(null);
  /* Intention venue d'AILLEURS (l'Accueil pose un mouvement) : même mécanique
     que PersonnelHub — bon onglet, modale s'il y a lieu, consommée une fois. */
  React.useEffect(() => {
    if (!intent) return;
    if (intent.tab) setTab(intent.tab);
    if (intent.action === 'add') setMoveIntent({ action: 'add', kind: 'hire' });
    if (onIntentConsumed) onIntentConsumed();
  }, [intent]);  // eslint-disable-line
  // Même préférence d'unité que les autres sections People (par tenant).
  const [unit, setUnit] = usePersistentValue(`people_unit_${activeTenant?.id || ''}`, 'chf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const TABS = resolveTabs(TAB_DEFS, t);

  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'summary':
        return <HeadcountSummaryPage embedded unit={u} activeTenant={activeTenant} year={year} addToast={addToast}
                 onAddPerson={() => onGoPeople && onGoPeople({ tab: 'people', action: 'add' })}
                 onAddMove={() => { setTab('moves'); setMoveIntent({ action: 'add', kind: 'hire' }); }}
                 onGoContracts={() => onGoPeople && onGoPeople({ tab: 'people' })} />;
      case 'actual':
        return <HrHeadcountPage embedded unit={u} activeTenant={activeTenant} year={year} />;
      case 'moves':
        return <HrPlanningPage embedded part="moves" unit={u} forExport={forExport} activeTenant={activeTenant}
                               setPage={setPage} year={year} onYearChange={onYearChange}
                               intent={forExport ? null : moveIntent}
                               onIntentConsumed={() => setMoveIntent(null)} />;
      case 'planned':
        return <HrPlanningPage embedded part="plan" unit={u} forExport={forExport} activeTenant={activeTenant}
                               setPage={setPage} year={year} onYearChange={onYearChange} />;
      default:
        return null;
    }
  };

  /* La synthèse est écartée de l'export : ses tuiles et sa frise sont des
     <div>, seul son tableau de mouvements sortirait — une feuille trompeuse. */
  const excel = useHubExcelExport({
    tabs: TABS.filter(tb => tb.id !== 'summary')
      .map(tb => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: 'Effectif',
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run]));

  return (
    <PageShell framed kind="report" unit={pageUnit} title={t('nav:items.hr-headcount')}>
      {/* La ligne d'onglets ne garde que ce qui AGIT sur l'écran : les entrées
          de la section sont descendues DANS la page, en tuiles au-dessus de la
          Synthèse (2026-09-01). Trois boutons serrés contre les onglets se
          lisaient comme des onglets de plus. */}
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div className="page-shell__panel">{renderTab(tab)}</div>
      {excel.stage}
    </PageShell>
  );
}
